import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { SourceImageApi } from "../api/source-image-api";
import { queryClient } from "./query-client";
import Toast from "@/lib/components/Toast";

export function useSourceImagesByOriginVideoIdQuery(
  teamExternalId: string,
  videoExternalId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: sourceImageQueryKeys.getByOriginVideoExternalId(
      teamExternalId,
      videoExternalId,
    ),
    queryFn: () =>
      SourceImageApi.getSourceImagesByOriginVideoExternalId(
        teamExternalId,
        videoExternalId,
      ),
    enabled: (options?.enabled ?? true) && !!videoExternalId,
    staleTime: 0,
    meta: {
      errorMessage: "Failed to fetch source images",
    },
  });
}

type SourceImageCursor = { createdAt: string; externalId: string } | undefined;

export function usePagedSourceImagesQuery(teamExternalId: string) {
  return useInfiniteQuery({
    queryKey: sourceImageQueryKeys.paged(teamExternalId),
    queryFn: ({ pageParam }) => {
      return SourceImageApi.getPagedSourceImages(teamExternalId, {
        pageSize: 50,
        cursorCreatedAt: pageParam?.createdAt,
        cursorExternalId: pageParam?.externalId,
      });
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as SourceImageCursor,
    staleTime: 0,
  });
}

export function useDeleteSourceImagesMutation({
  teamExternalId,
  onSuccess,
}: {
  teamExternalId: string;
  onSuccess?: () => void;
}) {
  return useMutation({
    mutationKey: sourceImageQueryKeys.delete(teamExternalId),
    mutationFn: (externalIds: string[]) =>
      SourceImageApi.deleteSourceImages(teamExternalId, externalIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: sourceImageQueryKeys.all(),
      });
      onSuccess?.();
    },
    onError: () => {
      Toast.error("Failed to delete images");
    },
  });
}

export const sourceImageQueryKeys = {
  all: () => ["sourceImage"],
  paged: (teamExternalId: string) => [
    "sourceImage",
    "paged",
    { teamExternalId },
  ],
  getByOriginVideoExternalId: (
    teamExternalId: string,
    videoExternalId: string,
  ) => ["sourceImage", "list", { teamExternalId, videoExternalId }],
  delete: (teamExternalId: string) => [
    "sourceImage",
    "delete",
    { teamExternalId },
  ],
};
